/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-empty-function */
import { useEffect, useRef, useState, useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { NetworkStatus, useQuery } from '@apollo/client';

import { HealthCheckDocument } from 'src/gql/graphql';
import useUI from 'hooks/use-ui';
import { getLogger } from 'src/utils/datadog-logger';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

/**
 * Hook to measure network connection speed using health check query
 *
 * This hook:
 * - Measures the time it takes for the health check query to complete
 * - Returns 'slow' if response time > 1500ms
 * - Returns 'good' if response time <= 1500ms
 * - Only runs in kiosk mode when the feature flag is enabled
 * - Returns null when not in kiosk mode or feature flag is disabled
 * - Checks latency every 30 seconds when rendered
 * - Cleans up timer when unmounted to prevent memory leaks
 *
 */

export enum ConnectionQuality {
  GOOD = 'good',
  SLOW = 'slow',
}

const LATENCY_THRESHOLD_MS = 1500; // 1.5 seconds threshold for slow connection
const LATENCY_CHECK_INTERVAL_MS = 30000; // 30 seconds interval for periodic checks

const logger = getLogger('latency-check');

export const useLatencyCheck = (): { connectionQuality: ConnectionQuality | null } => {
  const UI = useUI();
  const startTimeRef = useRef<number | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { dispensary } = useDispensary();
  const [connectionQuality, setConnectionQuality] = useState<ConnectionQuality | null>(null);

  const flags = useFlags();
  const isKioskLatencyManagementEnabled = flags['fintech.platform.kiosk-checkout-latency-management'] ?? false;

  const { data: healthCheckData, loading, error, refetch, networkStatus } = useQuery(HealthCheckDocument, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip: !UI.isKiosk || !isKioskLatencyManagementEnabled,
  });

  const performLatencyCheck = useCallback((): void => {
    if (!UI.isKiosk || !isKioskLatencyManagementEnabled) {
      return;
    }

    if (startTimeRef.current !== null) {
      return;
    }

    startTimeRef.current = performance.now();
    void refetch();
  }, [UI.isKiosk, isKioskLatencyManagementEnabled, refetch]);

  // Set up periodic latency checks
  useEffect(() => {
    if (!UI.isKiosk || !isKioskLatencyManagementEnabled) {
      setConnectionQuality(null);
      return () => {};
    }

    performLatencyCheck();

    // Set up periodic checks every 30 seconds
    timeoutRef.current = setInterval(performLatencyCheck, LATENCY_CHECK_INTERVAL_MS);

    // Cleanup function to clear the interval when component unmounts
    return () => {
      if (timeoutRef.current) {
        clearInterval(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [UI.isKiosk, isKioskLatencyManagementEnabled, performLatencyCheck]);

  // Handle query results and errors
  useEffect(() => {
    if (!UI.isKiosk || !isKioskLatencyManagementEnabled) {
      setConnectionQuality(null);
      return () => {};
    }

    // Start timing when a refetch begins
    if (networkStatus === NetworkStatus.refetch && !startTimeRef.current) {
      startTimeRef.current = performance.now();
    }

    // Measure latency when query completes successfully
    if (
      networkStatus === NetworkStatus.ready &&
      healthCheckData?.healthCheck?.status === 'ok' &&
      startTimeRef.current
    ) {
      const endTime = performance.now();
      const latencyMs = endTime - startTimeRef.current;

      const quality = latencyMs > LATENCY_THRESHOLD_MS ? ConnectionQuality.SLOW : ConnectionQuality.GOOD;

      if (quality === ConnectionQuality.SLOW) {
        logger.warn('Slow connection detected', {
          dispensaryId: dispensary?.id,
          dispensaryName: dispensary?.name,
          latencyMs,
          uiVariant: UI.variant,
        });
      }

      setConnectionQuality(quality);

      startTimeRef.current = null;
    }

    // Reset timer when query completes with non-ok status
    if (
      networkStatus === NetworkStatus.ready &&
      healthCheckData?.healthCheck?.status !== 'ok' &&
      startTimeRef.current
    ) {
      logger.warn('Health check completed with non-ok status', {
        dispensaryId: dispensary?.id,
        dispensaryName: dispensary?.name,
        status: healthCheckData?.healthCheck?.status,
        uiVariant: UI.variant,
      });
      setConnectionQuality(ConnectionQuality.GOOD); // Default to good on non-ok status
      startTimeRef.current = null;
    }

    // Handle errors by defaulting to 'good' connection quality
    if (error && startTimeRef.current) {
      logger.error('Latency check failed', {
        dispensaryId: dispensary?.id,
        dispensaryName: dispensary?.name,
        error,
        uiVariant: UI.variant,
      });
      setConnectionQuality(ConnectionQuality.GOOD); // Default to good on error
      startTimeRef.current = null;
    }

    return () => {};
  }, [
    UI,
    isKioskLatencyManagementEnabled,
    loading,
    healthCheckData,
    error,
    dispensary?.id,
    dispensary?.name,
    networkStatus,
  ]);

  return { connectionQuality };
};
